/**
 * Canvas Engine SOT — Variable Resolution
 *
 * Unified variable substitution extracted from editor.js and render-worker.ts.
 * Uses the regex pattern that handles ALL valid characters (from render-worker.ts),
 * rather than the stricter `[A-Za-z0-9_]` from editor.js.
 */

/**
 * Scan a string for `{{variable}}` tokens and return their names.
 * Uses the permissive regex to match any non-closing-brace content.
 */
export function scanForVars(str: string): string[] {
  const re = /\{\{([^}]+)\}\}/g;
  const names = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(str))) {
    names.add(m[1].trim());
  }
  return Array.from(names);
}

/**
 * Replace `{{variable}}` tokens in a string with values from a vars object.
 * If a variable is not found, it is replaced with empty string.
 *
 * This is the unified replacement function merging:
 * - editor.js `replaceVars()` (uses `[A-Za-z0-9_]`)
 * - render-worker.ts `applyVariables()` (uses `[^}]+`)
 *
 * We use the permissive `[^}]+` to support variable names with special chars.
 */
const VARIABLE_TOKEN_RE = /\{\{([^}]+)\}\}/g;
const DEFAULT_VARIABLE_RESOLUTION_PASSES = 6;

export interface ApplyVariablesOptions {
  maxPasses?: number;
  onMissing?: (key: string, token: string) => string;
}

function replaceVariablesOnce(
  text: string,
  vars: Record<string, string | number | null | undefined>,
  onMissing: (key: string, token: string) => string,
): { hadTokens: boolean; replacedKnown: boolean; value: string } {
  let hadTokens = false;
  let replacedKnown = false;

  const value = text.replace(VARIABLE_TOKEN_RE, (token, key: string) => {
    hadTokens = true;
    const trimmed = key.trim();
    if (Object.prototype.hasOwnProperty.call(vars, trimmed)) {
      replacedKnown = true;
      const resolved = vars[trimmed];
      return resolved == null ? '' : String(resolved);
    }
    return onMissing(trimmed, token);
  });

  return { hadTokens, replacedKnown, value };
}

export function applyVariables(
  text: string | null | undefined,
  vars: Record<string, string | number | null | undefined>,
  options: ApplyVariablesOptions = {},
): string {
  if (text == null) return '';

  const maxPasses = Number.isFinite(options.maxPasses) && Number(options.maxPasses) > 0
    ? Math.floor(Number(options.maxPasses))
    : DEFAULT_VARIABLE_RESOLUTION_PASSES;
  const onMissing = typeof options.onMissing === 'function'
    ? options.onMissing
    : () => '';

  let current = String(text);
  const seen = new Set<string>();

  for (let pass = 0; pass < maxPasses; pass += 1) {
    if (seen.has(current)) break;
    seen.add(current);

    const { hadTokens, replacedKnown, value } = replaceVariablesOnce(current, vars, onMissing);
    if (!hadTokens) return current;
    if (value === current || !replacedKnown) return value;
    current = value;
  }

  return current;
}

/**
 * Resolve a SnapSheet-style bound variable name to a runtime value.
 */
export function resolveBoundVariable(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined> | null | undefined,
): string | number | null | undefined {
  if (!vars || typeof binding !== 'string') return undefined;
  const key = binding.trim();
  if (!key) return undefined;
  return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : undefined;
}

/**
 * Resolve a bound variable to a string fallback.
 */
export function resolveBoundString(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined> | null | undefined,
  fallback: string,
): string {
  const resolved = resolveBoundVariable(binding, vars);
  if (resolved == null) return fallback;
  const normalized = String(resolved).trim();
  return normalized || fallback;
}

/**
 * Resolve a bound variable to a boolean fallback.
 */
export function resolveBoundBoolean(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined> | null | undefined,
  fallback: boolean,
): boolean {
  const resolved = resolveBoundVariable(binding, vars);
  if (resolved == null) return fallback;
  if (typeof resolved === 'boolean') return resolved;
  if (typeof resolved === 'number') return resolved !== 0;
  const normalized = String(resolved).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') return false;
  return fallback;
}

/**
 * Resolve a bound variable to a number fallback with optional clamping.
 */
export function resolveBoundNumber(
  binding: string | null | undefined,
  vars: Record<string, string | number | null | undefined> | null | undefined,
  fallback: number,
  options?: { min?: number; max?: number },
): number {
  const resolved = resolveBoundVariable(binding, vars);
  const parsed = Number(resolved);
  if (!Number.isFinite(parsed)) return fallback;
  const min = typeof options?.min === 'number' ? options.min : undefined;
  const max = typeof options?.max === 'number' ? options.max : undefined;
  if (typeof min === 'number' && parsed < min) return min;
  if (typeof max === 'number' && parsed > max) return max;
  return parsed;
}

// Alias for API compatibility
export const replaceVars = applyVariables;
